import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string
  name: string
  type?: string
  placeholder?: string
  formik?: any // Formik is optional
  icon?: React.ReactNode
  className?: string
  inputClassName?: string
  value?: string // For standalone mode: use as a controlled input
  validate?: (value: string) => string | undefined // Optional validation function for standalone
  disabled?: boolean // New prop for handling disabled state
  useOwnError?: boolean // prop for display errors inside input component
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      type = 'text',
      placeholder,
      formik,
      icon,
      className,
      inputClassName,
      value = '', // Default value used in standalone mode
      onChange,
      validate,
      disabled = false, // Default is not disabled
      useOwnError = true,
      ...rest
    },
    ref
  ) => {
    // State for standalone mode (Formik-independent mode)
    const [inputValue, setInputValue] = useState<string>(value)
    const [touched, setTouched] = useState(false)
    const [error, setError] = useState<string | undefined>(undefined)

    // Helper to determine if the component should use Formik or standalone logic
    const isUsingFormik = !!formik

    // Check for errors (Formik or standalone mode)
    const hasError = isUsingFormik
      ? formik.touched?.[name] && formik.errors?.[name] // If using Formik, read from its props
      : touched && error // Otherwise, fallback on standalone state

    // Handle value changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value

      if (isUsingFormik) {
        // If Formik is provided, trigger Formik handlers
        formik.handleChange(e)
      } else {
        // Update internal state for standalone usage
        setInputValue(newValue)
        if (validate) setError(validate(newValue)) // Perform validation if provided
      }
    }

    // Handle blur event
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isUsingFormik) {
        // If Formik is provided, trigger its onBlur handler
        formik.handleBlur(e)
      } else {
        // Handle blur for standalone mode
        setTouched(true)
        if (validate) setError(validate(inputValue)) // Perform validation on blur
      }
    }

    // Determine the input value (Formik or standalone)
    const inputCurrentValue = isUsingFormik
      ? formik.values?.[name] || '' // If using Formik, take value from Formik's state
      : value // Otherwise, fallback to the internal state

    return (
      <div className={twMerge('relative mb-4 h-full', className)}>
        {placeholder && (
          <label
            htmlFor={id}
            className="mb-2 block font-medium text-gray-900 dark:text-white"
          >
            {placeholder}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={id}
            name={name}
            type={type}
            placeholder={placeholder}
            disabled={disabled} // Add disabled attribute
            className={twMerge(
              'w-full rounded-lg border py-3 bg-transparent px-6 text-black outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary',
              icon && 'pl-10',
              hasError && 'border-red-500',
              !hasError && 'border-stroke',
              disabled &&
                'cursor-not-allowed bg-gray-100 disabled:bg-gray-200 dark:bg-gray-600',
              inputClassName
            )}
            value={inputCurrentValue}
            onChange={onChange ? onChange : handleInputChange}
            onBlur={handleBlur}
            {...rest}
          />

          {icon && (
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              {icon}
            </div>
          )}
        </div>

        {hasError && useOwnError && (
          <span className="text-red-500 text-sm">
            {isUsingFormik ? formik.errors?.[name] : error}{' '}
            {/* Display error depending on mode */}
          </span>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
